import styled from 'styled-components';
import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import { Divider } from '_components/atomic/layouts';
import ProcessSection from '_components/composed/sections/ProcessSection';
import WhyShouldUseUsSection from '_components/composed/sections/WhyShouldUseUsSection';
import ComparingSection from '_components/composed/sections/ComparingSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import MainBannerSection from '_components/composed/sections/MainBannerSection';
import TestimonialsSection from '_components/composed/sections/TestimonialsSection';

const { colors, spaces } = theme;

const StyledAboutPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AboutPage = () => {
  return (
    <StyledAboutPage>
      <MainBannerSection
        title="About us"
        topText="Welcome to Incorpified, your one-stop-shop for all your business incorporation needs. We understand the challenges and complexities that come with starting a business and our goal is to make the incorporation process as simple and stress-free as possible. Whether you're just starting out and need help incorporating, or already have a business and need assistance with ongoing compliance and legal requirements, we've got you covered. Our team of experts has years of experience in the field and is dedicated to providing personalized service and guidance every step of the way."
        bottomText="From LLCs to C-Corporations, we'll work with you to determine the best structure for your business and handle all the necessary paperwork and filing. We also provide other services such as Registered Agent, Annual Report, trademark registration, bookkeeping, merchant processing and more to help you stay compliant and manage your business efficiently. Let us take care of the legal and financial side of things so you can focus on growing your business."
        desktopImage={`${config.basePath}/images/about-banner-desktop.png`}
        mobileImage={`${config.basePath}/images/about-banner-mobile.png`}
        buttonsBlock={
          <Button
            suffix={
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.white} />
            }
            color={colors.indigo[600]}
          >
            Start Your Business
          </Button>
        }
      />
      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ProcessSection
        title="Our Process"
        activeStepIdx={0}
        steps={[
          {
            title: 'STEP 01',
            subtitle: 'Tell us about your business',
            description: (
              <span>
                Our team of experts will work with you to determine the best
                structure for your business
              </span>
            ),
            imageSrc: `${config.basePath}/images/telecommuting.svg`,
            imageAlt: 'Cartoon working at laptop',
            reverse: false,
          },
          {
            title: 'STEP 02',
            subtitle: 'Fill out our simple questionnaire',
            description: (
              <span>
                Our easy-to-use questionnaire will gather all the necessary
                information for your incorporation
              </span>
            ),
            imageSrc: `${config.basePath}/images/forms.svg`,
            imageAlt: 'Animated LLC incorporation form',
            reverse: true,
          },
          {
            title: 'STEP 03',
            subtitle: 'Select desired filing package',
            description: (
              <span>
                Choose from our various filing packages that fit your business
                needs and budget
              </span>
            ),
            imageSrc: `${config.basePath}/images/puzzle.svg`,
            imageAlt: 'Putting Puzzle Pieces together',
            reverse: false,
          },
          {
            title: 'STEP 04',
            subtitle: 'Get incorporated with the state',
            description: (
              <span>
                Once you&apos;ve chosen your desired filing package, we&apos;ll
                immediately begin preparing and filing all necessary documents
                with the state, ensuring a smooth and efficient incorporation
                process.
              </span>
            ),
            imageSrc: `${config.basePath}/images/launch.svg`,
            reverse: true,
          },
        ]}
      />

      <Divider size={spaces.large9X} desktop />

      <WhyShouldUseUsSection />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ComparingSection />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />
      <TestimonialsSection
        title="Testimonials"
        testimonials={[
          {
            description:
              'Incorpified made the incorporation process so easy and stress-free. Their team of experts guided me every step of the way and I had my business officially incorporated in no time. I highly recommend them to any new business owner.',
            author: 'John Davis,',
            title: 'CEO of Davis Construction',
          },
          {
            description:
              "I've used Incorpified for all of my business's legal needs and have never been disappointed. They're reliable, affordable, and always have my best interests in mind.",
            author: 'Michael Johnson,',
            title: 'Owner of LMBPO Inc',
          },
          {
            description:
              'I have been very impressed with Incorpified, they made the whole process of incorporating my business very simple, stress-free and efficient. Their team of experts were always available to help and answer my questions. I highly recommend them to anyone looking to start a business.',
            author: 'Rachel Brown,',
            title: 'CEO of The Art Studio',
          },
        ]}
      />
      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />
      <BottomBannerSection
        header={<>Get Your Business Incorporated Today</>}
        footer={
          <>
            <p>Here are some services that business owners use</p>
            <div className="buttonsContainer">
              <Button
                suffix={
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={colors.indigo[600]}
                  />
                }
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Start Your LLC
              </Button>
              <Button
                suffix={
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={colors.indigo[600]}
                  />
                }
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Start Your Corporation
              </Button>
            </div>
          </>
        }
        moreSpace
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />
    </StyledAboutPage>
  );
};

export default AboutPage;
